import {
  Stack,
  TextField,
  Button,
  IconButton,
  MenuItem,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  AccordionActions,
  ButtonGroup,
  Typography,
  Alert
} from '@mui/material'
import { useEffect, useState } from 'react'
import {
  Add,
  AddPhotoAlternate,
  Delete,
  ExpandMore,
  HideImage
} from '@mui/icons-material'
import '../../../Style/Form.css'
import { getIcon } from './../Helpers/Icon.js'
import RichTextEditorComponent from '../../RichTextEditor/RichTextEditor.js'

const SectionButton = ({ section, setSection, loading }) => {
  const add = type => {
    setSection(prev => {
      switch (type) {
        case 'prompt':
          return {
            ...prev,
            pulsePrompts: [...prev.pulsePrompts, '']
          }
        case 'option':
          return {
            ...prev,
            pulseOptions: [
              ...prev.pulseOptions,
              { text: '', image: null, alt: '' }
            ]
          }
        case 'row':
          return {
            ...prev,
            pulseGrid: {
              ...prev.pulseGrid,
              rows: [...prev.pulseGrid.rows, '']
            }
          }
        case 'column':
          return {
            ...prev,
            pulseGrid: {
              ...prev.pulseGrid,
              columns: [...prev.pulseGrid.columns, '']
            }
          }
        case 'choice':
          return {
            ...prev,
            pulseChoice: [...prev.pulseChoice, '']
          }
        default:
          return prev
      }
    })
  }

  if (!section.isABTesting && !section.isMultipleChoice && !section.isChoice) {
    return (
      <Button
        variant='contained'
        onClick={() => add('prompt')}
        disabled={loading}
        startIcon={<Add />}
      >
        Add Prompt
      </Button>
    )
  } else if (section.isABTesting) {
    return (
      <Button
        variant='contained'
        onClick={() => add('option')}
        disabled={loading}
        startIcon={<Add />}
      >
        Add Option
      </Button>
    )
  } else if (section.isMultipleChoice) {
    return (
      <>
        <Button
          variant='contained'
          onClick={() => add('row')}
          disabled={loading}
          startIcon={<Add />}
        >
          Add Row
        </Button>
        <Button
          variant='outlined'
          onClick={() => add('column')}
          disabled={loading}
          startIcon={<Add />}
        >
          Add Column
        </Button>
      </>
    )
  } else {
    return (
      <Button
        variant='contained'
        onClick={() => add('choice')}
        disabled={loading}
        startIcon={<Add />}
      >
        Add Choice
      </Button>
    )
  }
}

const PulseSliderValues = ({ pulseValues, setPulseValues, loading }) => {
  return (
    <Accordion sx={{ width: '100%' }}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        Slider Options (All Sliders in this Pulse will share these options)
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1}>
          {pulseValues.map((val, index) => (
            <Stack
              direction='row'
              alignItems='center'
              sx={{ width: '100%' }}
              key={index + '_pulseValue'}
            >
              <TextField
                label={`Slider Option ${index + 1}`}
                variant='outlined'
                onChange={e => {
                  setPulseValues(prev => {
                    const updated = [...prev]
                    updated[index] = e.target.value
                    return updated
                  })
                }}
                disabled={loading}
                required
                size='small'
                fullWidth
                value={val}
                sx={{ mr: 1 }}
                inputProps={{ maxLength: 200 }}
              />
              <Tooltip title='Delete Slider Option'>
                <span>
                  <IconButton
                    variant='outlined'
                    onClick={() => {
                      setPulseValues(pulseValues.filter((_, i) => i !== index))
                    }}
                    disabled={loading || pulseValues.length <= 2}
                  >
                    <Delete />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          ))}
        </Stack>
      </AccordionDetails>
      <AccordionActions>
        {pulseValues.length < 10 && (
          <Button
            variant='contained'
            onClick={() => setPulseValues([...pulseValues, ''])}
            disabled={loading}
            startIcon={<Add />}
          >
            Add Option
          </Button>
        )}
      </AccordionActions>
    </Accordion>
  )
}

const TypeSwitch = ({ section, setSection }) => {
  const isFormValid = section => {
    if (
      !section.isABTesting &&
      !section.isMultipleChoice &&
      !section.isChoice
    ) {
      return section.pulsePrompts.every(
        prompt => !prompt || prompt.trim() === ''
      )
    } else if (section.isABTesting) {
      const hasEmpty = section.pulseOptions
        .map(option => option.text)
        .every(text => !text || text.trim() === '' || text.trim() === '<p></p>')
      const emptyImg = section.pulseOptions
        .map(opt => opt.image)
        .every(img => !img)
      return hasEmpty && emptyImg
    } else if (section.isMultipleChoice) {
      return (
        section.pulseGrid.rows.every(
          row => !row || row.trim() === '' || row.trim() === '<p></p>'
        ) && section.pulseGrid.columns.every(col => !col || col.trim() === '')
      )
    } else {
      return section.pulseChoice.every(
        text => !text || text.trim() === '' || text.trim() === '<p></p>'
      )
    }
  }
  const changeType = type => {
    setSection(prev => {
      let sec = { ...prev }

      sec.isABTesting = false
      sec.isMultipleChoice = false
      sec.isChoice = false
      switch (type) {
        case 'abTesting':
          sec.isABTesting = true
          break
        case 'multipleChoiceGrid':
          sec.isMultipleChoice = true
          break
        case 'multipleChoice':
          sec.isChoice = true
          break
      }

      sec.pulsePrompts = ['']
      sec.pulseOptions = [
        { text: '', image: null, alt: null },
        { text: '', image: null, alt: null }
      ]
      sec.pulseGrid = {
        rows: [''],
        columns: ['', '']
      }
      sec.pulseChoice = ['']
      return sec
    })
  }

  const buttons = [
    {
      type: 'slider',
      text: 'Slider',
      contained: !(
        section.isABTesting ||
        section.isMultipleChoice ||
        section.isChoice
      )
    },
    {
      type: 'abTesting',
      text: 'A/B Testing',
      contained: section.isABTesting
    },
    {
      type: 'multipleChoice',
      text: 'Multiple Choice',
      contained: section.isChoice
    },
    {
      type: 'multipleChoiceGrid',
      text: 'Multiple Choice Grid',
      contained: section.isMultipleChoice
    }
  ]

  return (
    <Tooltip
      slotProps={{
        tooltip: {
          sx: {
            backgroundColor: 'warning.main',
            color: 'warning.contrastText',
            display: isFormValid(section) ? 'none' : 'inherit',
            fontWeight: 'bold',
            '& .MuiTooltip-arrow': {
              color: 'warning.main'
            }
          }
        }
      }}
      arrow
      title='Changing Pulse Type will delete your options, images and/or prompts. To change Pulse Type delete all text and images.'
    >
      <>
        <ButtonGroup
          variant='outlined'
          aria-label='Section Type Toggle'
          fullWidth
          color='secondary'
          orientation='vertical'
          sx={{ pb: 2, display: { md: 'none', xs: 'inline-flex' } }}
          disabled={!isFormValid(section)}
        >
          {buttons.map(button => (
            <Button
              key={button.text + '_mobile'}
              variant={button.contained ? 'contained' : 'outlined'}
              onClick={_e => {
                changeType(button.type)
              }}
            >
              {button.text}
            </Button>
          ))}
        </ButtonGroup>
        <ButtonGroup
          variant='outlined'
          aria-label='Section Type Toggle'
          fullWidth
          color='secondary'
          sx={{ pb: 2, display: { xs: 'none', md: 'inline-flex' } }}
          disabled={!isFormValid(section)}
        >
          {buttons.map(button => (
            <Button
              key={button.text + '_desktop'}
              variant={button.contained ? 'contained' : 'outlined'}
              onClick={_e => {
                changeType(button.type)
              }}
            >
              {button.text}
            </Button>
          ))}
        </ButtonGroup>
      </>
    </Tooltip>
  )
}

const PulseSectionInputs = ({
  section,
  index,
  pulseHeaders,
  pulseSections,
  setPulseSections,
  loading,
  imageRefs,
  abImageRefs,
  handleABImageChange,
  handleImageChange,
  sectionImages,
  handleImageAltChange,
  handleImageRemove,
  handleABImageRemove,
  handleSectionImageDelete,
  pulseValues,
  setPulseValues,
  showSubmitModal,
  allExpand
}) => {
  const [internalPulseValues, setInternalPulseValues] = useState(pulseValues)
  const [internalSection, setInternalSection] = useState(section)
  const [sectionValid, setSectionValid] = useState(false)
  const [expanded, setExpanded] = useState(allExpand)

  useEffect(() => {
    setExpanded(allExpand)
  }, [allExpand])

  useEffect(() => {
    setInternalPulseValues(pulseValues)
  }, [pulseValues])

  useEffect(() => {
    setInternalSection(section)
  }, [section])

  const updateParent = () => {
    console.log('Updating Parent')
    setPulseValues(internalPulseValues)
    setPulseSections(prev => {
      let newSections = [...prev]
      newSections[index] = internalSection
      return newSections
    })
  }

  const handleBlur = event => {
    const currentTarget = event.currentTarget

    setTimeout(() => {
      if (!currentTarget.contains(document.activeElement)) {
        updateParent()
      }
    }, 0)
  }

  useEffect(() => {
    if (pulseSectionsAreValid()) {
      setSectionValid(true)
    } else {
      setSectionValid(false)
    }
  }, [internalSection])

  useEffect(() => {
    updateParent()
  }, [showSubmitModal])

  const pulseSectionsAreValid = () => {
    if (!internalSection.title) {
      return false
    }
    if (
      !internalSection.isABTesting &&
      !internalSection.isMultipleChoice &&
      !internalSection.isChoice
    ) {
      for (let prompt of internalSection.pulsePrompts) {
        if (!prompt) {
          return false
        }
      }
    } else if (internalSection.isABTesting) {
      const texts = internalSection.pulseOptions.map(option => option.text)

      if (texts.some(text => !text || text.trim() === '<p></p>')) {
        return false
      }

      const uniqueTexts = new Set(texts)
      if (uniqueTexts.size !== texts.length) {
        return false
      }
    } else if (internalSection.isMultipleChoice) {
      let rowsGood = internalSection.pulseGrid.rows.every(
        row => row && row.trim() !== '' && row.trim() !== '<p></p>'
      )
      let colsGood = internalSection.pulseGrid.columns.every(
        col => col && col.trim() !== ''
      )
      if (!rowsGood || !colsGood) {
        return false
      }
      const uniqueCols = new Set(internalSection.pulseGrid.columns)
      if (uniqueCols.size !== internalSection.pulseGrid.columns.length) {
        return false
      }
    } else {
      const texts = internalSection.pulseChoice

      if (texts.some(text => !text || text.trim() === '<p></p>')) {
        return false
      }

      const uniqueTexts = new Set(texts)
      if (uniqueTexts.size !== texts.length) {
        return false
      }
    }
    if (pulseHeaders[0].title && pulseHeaders[0].icon) {
      if (
        !internalSection.header ||
        !internalSection.header.icon ||
        !internalSection.header.title
      ) {
        return false
      }
    }
    return true
  }

  return (
    <Accordion
      expanded={expanded}
      defaultExpanded
      onChange={() => setExpanded(prev => !prev)}
      sx={{ width: '100%', marginBlock: 1 }}
      onBlur={handleBlur}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{ '& .MuiAccordionSummary-content': { width: '100%' } }}
      >
        <Stack rowGap={1} sx={{ width: '100%' }}>
          Section {index + 1}
          {sectionValid ? (
            <Alert sx={{ width: '100%' }} severity='success'>
              Section {index + 1} is valid!
            </Alert>
          ) : (
            <Alert severity='error' sx={{ width: '100%' }}>
              Section {index + 1} is not valid. Please make sure to fill in all
              required fields.
              {internalSection.isABTesting && (
                <>
                  <br />
                  Option Text must be unique for A/B Testing
                </>
              )}
              {internalSection.isMultipleChoice && (
                <>
                  <br />
                  Column Text must be unique for Multiple Choice Grid
                </>
              )}
              {internalSection.isChoice && (
                <>
                  <br />
                  Choice Text must be unique for Multiple Choice
                </>
              )}
            </Alert>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {pulseHeaders[0].title && pulseHeaders[0].icon && (
          <TextField
            select
            label='Section Header'
            size='small'
            error={
              !internalSection.header ||
              !internalSection.header.icon ||
              !internalSection.header.title
            }
            fullWidth
            required
            disabled={loading}
            value={internalSection.header ? internalSection.header?.title : ''}
            sx={{ mb: 1 }}
          >
            {pulseHeaders
              .filter(val => val.icon && val.title)
              .map(option => (
                <MenuItem
                  key={option.title}
                  value={option.title}
                  onClick={() => {
                    setInternalSection(prev => ({
                      ...prev,
                      header: option
                    }))
                  }}
                >
                  {getIcon(option.icon)}
                  {option.title}
                </MenuItem>
              ))}
          </TextField>
        )}
        <TextField
          label='Section Title'
          variant='outlined'
          onChange={e => {
            setInternalSection(prev => ({
              ...prev,
              title: e.target.value
            }))
          }}
          disabled={loading}
          required
          size='small'
          fullWidth
          value={internalSection.title}
          sx={{ mb: 1 }}
        />
        <RichTextEditorComponent
          label='Section Subtitle'
          onChange={value => {
            setInternalSection(prev => ({
              ...prev,
              subtitle: value
            }))
          }}
          disabled={loading}
          value={internalSection.subtitle}
          mb={1}
          keyValue={'pulse_section' + index + '_subtitle_desctiption_rte'}
        />
        <input
          style={{ display: 'none' }}
          ref={el => (imageRefs.current[index] = el)}
          id='logo'
          type='file'
          accept='.png, .jpg, .jpeg'
          onChange={e => handleImageChange(index, e)}
        />
        {sectionImages &&
        sectionImages[index] &&
        sectionImages[index].preview ? (
          <Stack alignItems='center' sx={{ mb: 2 }}>
            <img
              style={{
                height: '125px',
                margin: '10px auto',
                objectFit: 'contain'
              }}
              src={sectionImages[index].preview}
              className='logoUpload'
              alt='Section image preview'
            />
            <Stack
              sx={{ width: '100%' }}
              direction='row'
              spacing={1}
              alignItems='center'
            >
              <TextField
                label='Section Image Alt Text'
                variant='outlined'
                onChange={e => {
                  handleImageAltChange(index, e)
                }}
                disabled={loading}
                size='small'
                fullWidth
                value={sectionImages[index].alt || ''}
                sx={{ mb: 1 }}
              />
              <Button
                variant='outlined'
                sx={{
                  width: 'fit-content',
                  whiteSpace: 'nowrap',
                  minWidth: '145px'
                }}
                onClick={() => handleImageRemove(index)}
                disabled={loading}
                color='error'
              >
                Remove Image
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Stack alignItems='center' sx={{ mb: 1 }}>
            <Button
              variant='contained'
              onClick={() =>
                imageRefs.current &&
                imageRefs.current[index] &&
                imageRefs.current[index].click()
              }
              disabled={loading}
              startIcon={<AddPhotoAlternate />}
            >
              Upload Section Image
            </Button>
          </Stack>
        )}
        <Stack spacing={2} sx={{ mt: 2 }}>
          <TypeSwitch
            section={internalSection}
            setSection={setInternalSection}
          />
          {!internalSection.isABTesting &&
            !internalSection.isMultipleChoice &&
            !internalSection.isChoice && (
              <PulseSliderValues
                setPulseValues={setInternalPulseValues}
                pulseValues={internalPulseValues}
                loading={loading}
              />
            )}
          {!internalSection.isABTesting &&
          !internalSection.isMultipleChoice &&
          !internalSection.isChoice ? (
            internalSection.pulsePrompts.map((val, index2) => (
              <Stack
                direction='row'
                alignItems='center'
                sx={{ width: '100%' }}
                key={index + '_section_' + index2 + '_pulsePrompt'}
              >
                <TextField
                  label={`Prompt ${index2 + 1}`}
                  variant='outlined'
                  onChange={e => {
                    setInternalSection(prev => ({
                      ...prev,
                      pulsePrompts: prev.pulsePrompts.map((p, i) =>
                        i === index2 ? e.target.value : p
                      )
                    }))
                  }}
                  disabled={loading}
                  required
                  size='small'
                  fullWidth
                  value={val}
                  sx={{ mr: 1 }}
                />

                <Tooltip title='Delete Prompt'>
                  <span>
                    <IconButton
                      variant='outlined'
                      onClick={() => {
                        setInternalSection(prev => ({
                          ...prev,
                          pulsePrompts: prev.pulsePrompts.filter(
                            (_, i) => i !== index2
                          )
                        }))
                      }}
                      disabled={
                        loading || internalSection.pulsePrompts.length <= 1
                      }
                    >
                      <Delete />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            ))
          ) : internalSection.isABTesting ? (
            internalSection.pulseOptions.map((option, index2) => (
              <Stack
                key={'sectionNumber_' + index + 'option_' + index2}
                spacing={1}
                sx={{ pb: 1 }}
              >
                <Typography className='rteLabel' type='span'>
                  {'Option ' + (index2 + 1)}
                </Typography>
                {internalSection.pulseOptions[index2].preview && (
                  <img
                    style={{
                      height: '100px',
                      margin: '10px auto',
                      objectFit: 'contain'
                    }}
                    src={internalSection.pulseOptions[index2].preview}
                    className='logoUpload'
                    alt={'Pulse option ' + index2 + ' image preview'}
                  />
                )}
                <Stack
                  direction='row'
                  alignItems='center'
                  sx={{ width: '100%' }}
                  key={index + '_section_' + index2 + '_pulseOption'}
                >
                  <RichTextEditorComponent
                    label={`Option ${index2 + 1} Text`}
                    onChange={value => {
                      setInternalSection(prev => ({
                        ...prev,
                        pulseOptions: prev.pulseOptions.map((opt, i) =>
                          i === index2 ? { ...opt, text: value } : opt
                        )
                      }))
                    }}
                    disabled={loading}
                    required
                    value={option.text}
                    mr={1}
                    keyValue={
                      'pulse_section' + index + '_option' + index2 + '_rte'
                    }
                    buttons={[
                      !option.preview ? (
                        <Tooltip title='Add Image'>
                          <span>
                            <IconButton
                              color='primary'
                              onClick={() => {
                                if (
                                  abImageRefs.current &&
                                  abImageRefs.current[
                                    `section${index}_option${index2}`
                                  ]
                                ) {
                                  abImageRefs.current[
                                    `section${index}_option${index2}`
                                  ].click()
                                }
                              }}
                            >
                              <AddPhotoAlternate />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : (
                        <Tooltip title='Delete Image'>
                          <span>
                            <IconButton
                              color='error'
                              onClick={() => {
                                handleABImageRemove(
                                  internalSection,
                                  setInternalSection,
                                  index,
                                  index2,
                                  false
                                )
                              }}
                            >
                              <HideImage />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ),

                      <Tooltip title='Delete Option'>
                        <span>
                          <IconButton
                            variant='outlined'
                            onClick={() => {
                              if (option.preview) {
                                handleABImageRemove(
                                  internalSection,
                                  setInternalSection,
                                  index,
                                  index2,
                                  true
                                )
                              } else {
                                setInternalSection(prev => ({
                                  ...prev,
                                  pulseOptions: prev.pulseOptions.filter(
                                    (_, i) => i !== index2
                                  )
                                }))
                              }
                            }}
                            disabled={
                              loading ||
                              internalSection.pulseOptions.length <= 2
                            }
                          >
                            <Delete />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ]}
                  />

                  <input
                    style={{ display: 'none' }}
                    ref={el =>
                      (abImageRefs.current[`section${index}_option${index2}`] =
                        el)
                    }
                    id={`section${index}_option${index2}`}
                    type='file'
                    accept='.png, .jpg, .jpeg'
                    onChange={e =>
                      handleABImageChange(index2, setInternalSection, e)
                    }
                  />
                </Stack>
                {option.preview && (
                  <TextField
                    label={`Option ${index2 + 1} Image Alt Text`}
                    variant='outlined'
                    onChange={e => {
                      setInternalSection(prev => ({
                        ...prev,
                        pulseOptions: prev.pulseOptions.map((opt, i) =>
                          i === index2 ? { ...opt, alt: e.target.value } : opt
                        )
                      }))
                    }}
                    disabled={loading}
                    size='small'
                    fullWidth
                    value={option.alt || ''}
                    sx={{ mr: 1 }}
                  />
                )}
              </Stack>
            ))
          ) : internalSection.isMultipleChoice ? (
            <Stack direction='column' gap={2}>
              {internalSection.pulseGrid.rows.map((row, index2) => (
                <Stack
                  direction='row'
                  alignItems='center'
                  key={index + '_sectionPulseGridRow' + index2}
                >
                  <RichTextEditorComponent
                    label={`Row ${index2 + 1} Text`}
                    onChange={value => {
                      setInternalSection(prev => ({
                        ...prev,
                        pulseGrid: {
                          ...prev.pulseGrid,
                          rows: prev.pulseGrid.rows.map((row, i) =>
                            i === index2 ? value : row
                          )
                        }
                      }))
                    }}
                    disabled={loading}
                    required
                    value={row}
                    mr={1}
                    keyValue={
                      'pulse_section' + index + '_row' + index2 + '_rte'
                    }
                    buttons={[
                      <Tooltip title='Delete Row'>
                        <span>
                          <IconButton
                            variant='outlined'
                            onClick={() => {
                              setInternalSection(prev => ({
                                ...prev,
                                pulseGrid: {
                                  ...prev.pulseGrid,
                                  rows: prev.pulseGrid.rows.filter(
                                    (_, i) => i !== index2
                                  )
                                }
                              }))
                            }}
                            disabled={
                              loading ||
                              internalSection.pulseGrid.rows.length <= 1
                            }
                          >
                            <Delete />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ]}
                  />
                </Stack>
              ))}
              {internalSection.pulseGrid.columns.map((col, index2) => (
                <Stack
                  direction='row'
                  key={index + '_sectionPulseGridColumn' + index2}
                >
                  <TextField
                    label={`Column ${index2 + 1}`}
                    variant='outlined'
                    onChange={e => {
                      setInternalSection(prev => ({
                        ...prev,
                        pulseGrid: {
                          ...prev.pulseGrid,
                          columns: prev.pulseGrid.columns.map((col, i) =>
                            i === index2 ? e.target.value : col
                          )
                        }
                      }))
                    }}
                    disabled={loading}
                    required
                    size='small'
                    fullWidth
                    value={col}
                    sx={{ mr: 1 }}
                  />

                  <Tooltip title='Delete Column'>
                    <span>
                      <IconButton
                        variant='outlined'
                        onClick={() => {
                          setInternalSection(prev => ({
                            ...prev,
                            pulseGrid: {
                              ...prev.pulseGrid,
                              columns: prev.pulseGrid.columns.filter(
                                (_, i) => i !== index2
                              )
                            }
                          }))
                        }}
                        disabled={
                          loading ||
                          internalSection.pulseGrid.columns.length <= 2
                        }
                      >
                        <Delete />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              ))}
            </Stack>
          ) : (
            internalSection.pulseChoice.map((val, index2) => (
              <Stack
                direction='row'
                alignItems='center'
                sx={{ width: '100%' }}
                key={index + '_section_' + index2 + '_pulseChoice'}
              >
                <RichTextEditorComponent
                  label={`Choice ${index2 + 1}`}
                  onChange={value => {
                    setInternalSection(prev => ({
                      ...prev,
                      pulseChoice: prev.pulseChoice.map((p, i) =>
                        i === index2 ? value : p
                      )
                    }))
                  }}
                  disabled={loading}
                  required
                  value={val}
                  mr={1}
                  keyValue={
                    'pulse_section' + index + '_choice' + index2 + '_rte'
                  }
                  buttons={[
                    <Tooltip title='Delete Choice'>
                      <span>
                        <IconButton
                          variant='outlined'
                          onClick={() => {
                            setInternalSection(prev => ({
                              ...prev,
                              pulseChoice: prev.pulseChoice.filter(
                                (_, i) => i !== index2
                              )
                            }))
                          }}
                          disabled={
                            loading || internalSection.pulseChoice.length <= 1
                          }
                        >
                          <Delete />
                        </IconButton>
                      </span>
                    </Tooltip>
                  ]}
                />
              </Stack>
            ))
          )}
        </Stack>
      </AccordionDetails>
      <AccordionActions>
        {pulseSections.length > 1 && (
          <Tooltip title='Delete Section'>
            <Button
              color='error'
              sx={{ marginRight: 'auto' }}
              startIcon={<Delete />}
              onClick={() => {
                handleSectionImageDelete(index)
              }}
            >
              Delete
            </Button>
          </Tooltip>
        )}
        <SectionButton
          section={internalSection}
          setSection={setInternalSection}
          loading={loading}
        />
      </AccordionActions>
    </Accordion>
  )
}

export default PulseSectionInputs
